// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  DISTRIBUTOR: '/distributor',
  SETUP: '/setup',
  FOLDERS: '/folder',
  EQUIPMENTS: '/equipment',
  TAGS: '/tags',
  MODELS: '/models',
  TRAINING: '/training',
  MEDICAL_VISIT: '/medical-visit',
  HABILITATION: '/habilitation',
  ORG_DOCUMENT: '/org-documents',
  REPORTS: '/report',
  USERS: '/users',
  TRANSFERS: '/transfers',
  REPAIRS: '/repairs',
  SITES: '/sites',
  INCIDENTS: '/incident',
  SETTINGS: '/settings',
  SUPPORT: '/support',
  AGENT: '/agent',
  ADMIN: '/admin',
  IMPORT_EXPORT: '/import-export',
  SHORTLINK: '/i',
  TRANSFER_CHECK: '/transfer/check', //never change for backward compatibility
};

// ----------------------------------------------------------------------

export const paths = {
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
    register: `${ROOTS.AUTH}/register`, //email register (step1)
    retrievePassword: `${ROOTS.AUTH}/retrievePassword`,
    emailValidation: `${ROOTS.AUTH}/emailValidation`,
    connect: `${ROOTS.AUTH}/connect`,
  },
  setup: ROOTS.SETUP, //account setup (step 2)
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
  },
  distributor: {
    root: ROOTS.DISTRIBUTOR,
    organizations: `${ROOTS.DISTRIBUTOR}/organizations`,
    users: `${ROOTS.DISTRIBUTOR}/users`,
    equipments: `${ROOTS.DISTRIBUTOR}/equipments`,
    references: `${ROOTS.DISTRIBUTOR}/references`,
    referencesTop: `${ROOTS.DISTRIBUTOR}/references/top`,
    referencesOpportunities: `${ROOTS.DISTRIBUTOR}/references/opportunities`,
    referencesInspections: `${ROOTS.DISTRIBUTOR}/references/inspections`,
    referencesNew: `${ROOTS.DISTRIBUTOR}/references/new`,
  },
  //Shortlink
  shortlink: (id: string) => `${ROOTS.SHORTLINK}/${id}`, //equipment shortlink
  transferConfirm: (externalId: string) =>
    `${ROOTS.TRANSFER_CHECK}/${externalId}`, //transfer confirmation shortlink
  //IMPORT
  importExport: ROOTS.IMPORT_EXPORT,
  // FOLDERS
  folder: {
    root: ROOTS.FOLDERS,
    new: (parentId?: number) =>
      `${ROOTS.FOLDERS}/new${parentId ? `?parentId=${parentId}` : ''}`,
    view: (id: string | number) => `${ROOTS.FOLDERS}/${id}`,
    edit: (id: string | number) => `${ROOTS.FOLDERS}/${id}/edit`,
  },
  //TAGS
  tags: ROOTS.TAGS,
  //MODELS
  models: ROOTS.MODELS,
  // EQUIPMENTS
  equipment: {
    root: (folderId?: string | number, query?: string) =>
      `${folderId ? `${ROOTS.FOLDERS}/${folderId}` : ''}${ROOTS.EQUIPMENTS}${query ? `?${query}` : ''}`,
    new: (folderId?: string | number) =>
      `${folderId ? `${ROOTS.FOLDERS}/${folderId}` : ''}${ROOTS.EQUIPMENTS}/new`,
    duplicate: (id: string | number, folderId?: string | number) =>
      `${folderId ? `${ROOTS.FOLDERS}/${folderId}` : ''}${ROOTS.EQUIPMENTS}/new?duplicateFrom=${id}`,
    details: (id: string | number, folderId?: string | number) =>
      `${folderId ? `${ROOTS.FOLDERS}/${folderId}` : ''}${ROOTS.EQUIPMENTS}/${id}`,
    edit: (id: string | number, folderId?: string | number) =>
      `${folderId ? `${ROOTS.FOLDERS}/${folderId}` : ''}${ROOTS.EQUIPMENTS}/${id}/edit`,
    print: `${ROOTS.EQUIPMENTS}/print`,
  },
  //CHECKLIST
  checklist: {
    root: '/checklist',
    new: '/checklist/new',
    detail: (id: string | number) => `/checklist/${id}`,
    edit: (id: string | number) => `/checklist/${id}/edit`,
  },
  //INSPECTION
  inspection: {
    root: '/inspection',
    detail: (id: string | number) => `/inspection/${id}`,
  },
  //TRAINING
  training: {
    root: ROOTS.TRAINING,
    new: `${ROOTS.TRAINING}/new`,
    view: (id: string | number) => `${ROOTS.TRAINING}/${id}`,
    edit: (id: string | number) => `${ROOTS.TRAINING}/${id}/edit`,
  },
  //MEDICAL VISIT
  medicalVisit: {
    root: ROOTS.MEDICAL_VISIT,
    new: `${ROOTS.MEDICAL_VISIT}/new`,
    view: (id: string | number) => `${ROOTS.MEDICAL_VISIT}/${id}`,
    edit: (id: string | number) => `${ROOTS.MEDICAL_VISIT}/${id}/edit`,
    config: `${ROOTS.MEDICAL_VISIT}/config`,
  },
  //HABILITATION
  habilitation: {
    config: `${ROOTS.HABILITATION}/config`,
  },
  //ORG DOCUMENT
  orgDocument: {
    root: ROOTS.ORG_DOCUMENT,
    view: (id: string | number) => `${ROOTS.ORG_DOCUMENT}/${id}`,
    confirm: (token: string) => `${ROOTS.ORG_DOCUMENT}/confirm/${token}`,
  },
  //REPORT
  reports: {
    root: ROOTS.REPORTS,
    view: `${ROOTS.REPORTS}/view`,
  },
  //USERS
  users: {
    root: ROOTS.USERS,
    new: `${ROOTS.USERS}/new`,
    me: `${ROOTS.USERS}/me`,
    view: (id: string | number) => `${ROOTS.USERS}/${id}`,
    edit: (id: string | number) => `${ROOTS.USERS}/${id}/edit`,
  },
  //TRANSFERS
  transfers: {
    root: ROOTS.TRANSFERS,
    view: (id: string | number) => `${ROOTS.TRANSFERS}/${id}`,
  },
  repairs: {
    root: ROOTS.REPAIRS,
    view: (id: string | number) => `${ROOTS.REPAIRS}/${id}`,
  },
  repair: {
    root: ROOTS.REPAIRS,
    new: `${ROOTS.REPAIRS}/new`,
    view: (id: string | number) => `${ROOTS.REPAIRS}/${id}`,
    review: (id: string | number) => `${ROOTS.REPAIRS}/${id}/review`,
    reinspect: (id: string | number) => `${ROOTS.REPAIRS}/${id}/reinspect`,
  },
  //INCIDENTS
  incident: {
    root: ROOTS.INCIDENTS,
    new: `${ROOTS.INCIDENTS}/new`,
    view: (id: string | number) => `${ROOTS.INCIDENTS}/${id}`,
    config: `${ROOTS.INCIDENTS}/config`,
    settings: `${ROOTS.INCIDENTS}/settings`,
    // Printed on physical posters — never change this path.
    declarePublic: (token: string) => `/declare/${token}`,
  },
  //LOCATION
  sites: {
    root: ROOTS.SITES,
  },
  //SETTINGS
  settings: {
    user: ROOTS.SETTINGS,
    organization: `${ROOTS.SETTINGS}/organization`,
    subscription: `${ROOTS.SETTINGS}/subscription`,
    integrations: `${ROOTS.SETTINGS}/integrations`,
  },
  support: ROOTS.SUPPORT,
  agent: {
    root: ROOTS.AGENT,
    conversation: (publicId: string) => `${ROOTS.AGENT}/${publicId}`,
  },
  errors: {
    notFound404: '/404',
  },
  // SHORTLINK
  item: {
    root: (id: string | number) => `/i/${id}`,
  },
  //ADMIN
  admin: {
    import: `${ROOTS.ADMIN}/import`,
    actions: `${ROOTS.ADMIN}/actions`,
    data: `${ROOTS.ADMIN}/data`,
    organizations: `${ROOTS.ADMIN}/organizations`,
    mails: `${ROOTS.ADMIN}/mails`,
    brands: `${ROOTS.ADMIN}/brands`,
    billing: `${ROOTS.ADMIN}/billing`,
  },
};
